import React, { useState, useEffect } from "react";
import { notification } from "antd";
import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import TestRunDescription from "./Steps/TestRunDescription";
import TestRunConfig from "./Steps/TestRunConfig";
import TestRunEvaluations from "./Steps/TestRunEvaluations";
import TestRunReview from "./Steps/TestRunReview";
import { TestRunService } from "../../services/TestRunService";
import { usePermissions } from "../../PermissionsProvider";
import { Config } from "../../model_gen/config";
import { IntentGroup, TestRun, TestSet } from "../../model_gen/evaluation";
import { IntentService } from "../../services/IntentService";
import { TestSetService } from "../../services/TestSetService";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { 
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  PlayIcon
} from "lucide-react";
import { cn } from "../../lib/utils";

interface TestRunData {
  description?: string;
  config?: Config;
  test_set_id?: number;
  evaluation_criteria_ids?: number[];
}

interface StepItem {
  key: string;
  title: string;
  component: React.ReactNode;
}

export const NEW_TEST_RUN_STORAGE_KEY = "testRunData";

const TestRunNew: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { intentGroupId, testSetId } = useParams();
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [loading, setLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const step = searchParams.get("step");
    return step ? parseInt(step) : 0;
  });
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSet, setTestSet] = useState<TestSet | null>(null);
  const [testRunData, setTestRunData] = useState<TestRunData>(() => {
    const saved = localStorage.getItem(NEW_TEST_RUN_STORAGE_KEY);
    return saved ? JSON.parse(saved) : { test_set_id: Number(testSetId) };
  });
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>("");

  useEffect(() => {
    localStorage.setItem(NEW_TEST_RUN_STORAGE_KEY, JSON.stringify(testRunData));
  }, [testRunData]);

  useEffect(() => {
    // Only update URL if step parameter should be present (when step > 0 or step was already in URL initially)
    const currentStepParam = searchParams.get("step");
    const shouldHaveStepParam = currentStep > 0 || currentStepParam !== null;
    
    if (shouldHaveStepParam && currentStepParam !== currentStep.toString()) {
      const currentParams = Object.fromEntries(searchParams.entries());
      setSearchParams({
        ...currentParams,
        step: currentStep.toString()
      }, { replace: true }); // Use replace to avoid creating new history entries for step changes
    }
  }, [currentStep, searchParams, setSearchParams]);

  useEffect(() => {
    if (intentGroupId && testSetId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        false,
        setLoading,
        apiKey
      ).subscribe(
        (intentGroup: IntentGroup) => {
          setIntentGroup(intentGroup);
          setTestRunData((prev) => ({ ...prev, config: intentGroup.config }));
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [intentGroupId, apiKey]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSet(
        String(testSetId),
        setLoading,
        apiKey
      ).subscribe((testSet: TestSet) => {
        setTestSet(testSet);
      });
    }
  }, [testSetId, apiKey]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps: StepItem[] = [
    {
      key: "description",
      title: "Description",
      component: (
        <TestRunDescription
          description={testRunData.description}
          onChange={(description: string) =>
            setTestRunData((prev) => ({ ...prev, description }))
          }
        />
      ),
    },
    {
      key: "configuration",
      title: "Configuration",
      component: (
        <TestRunConfig
          intentGroupId={Number(intentGroupId)}
          testSetId={Number(testSetId)}
          config={testRunData.config}
          onChange={(config: Config) =>
            setTestRunData((prev) => ({ ...prev, config }))
          }
          onEndpointChange={(endpoint: string) => setSelectedEndpoint(endpoint)}
        />
      ),
    },
    {
      key: "evaluations",
      title: "Evaluations",
      component: (
        <TestRunEvaluations
          selectedCriteriaIds={testRunData.evaluation_criteria_ids || []}
          onCriteriaChange={(ids: number[]) =>
            setTestRunData((prev) => ({ ...prev, evaluation_criteria_ids: ids }))
          }
        />
      ),
    },
    {
      key: "review",
      title: "Review",
      component: (
        <TestRunReview
          testRunData={testRunData}
          testSetId={Number(testSetId)}
        />
      ),
    },
  ];

  const canProceedFromStep = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return (
          testRunData.description && testRunData.description.trim().length > 0
        );
      case 1:
        return testRunData.config !== undefined;
      case 2:
        return true; // evaluations step is optional
      case 3:
        return true; // review
      default:
        return false;
    }
  };

  const canProceed = () => canProceedFromStep(currentStep);

  const createTestRun = async () => {
    const payload: any = {
      description: testRunData.description,
      test_set_id: Number(testSetId),
      intent_group_id: Number(intentGroupId),
      config: testRunData.config,
    };
    if (
      testRunData.evaluation_criteria_ids &&
      testRunData.evaluation_criteria_ids.length > 0
    ) {
      payload.evaluation_criteria_ids = testRunData.evaluation_criteria_ids;
    }

    TestRunService.createTestRun(
      payload,
      setLoading,
      apiKey,
      selectedEndpoint
    ).subscribe(
      (response: TestRun) => {
        console.log(`Test run created successfully: ${response.id}`);
        localStorage.removeItem(NEW_TEST_RUN_STORAGE_KEY);
        notification.success({
          message: "Test run created successfully",
          description: "Your test run has been created successfully",
        });
        navigate(`/intent-group/${intentGroupId}/testsets/${testSetId}`);
      },
      (error: any) => {
        notification.error({
          message: "Error creating test run",
          description: error.message,
        });
      }
    );
  };

  const completedSteps = steps.slice(0, currentStep).filter((_, index) => {
    // Check if each previous step would be valid without causing re-renders
    return canProceedFromStep(index);
  }).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/intent-groups")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Intent Groups
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                {intentGroup && (
                  <>
                    <li>
                      <button 
                        onClick={() => navigate(`/intent-group/${intentGroup.id}`)}
                        className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                      >
                        {intentGroup.intent_name}
                      </button>
                    </li>
                    <li className="text-muted-foreground/40">
                      <span>/</span>
                    </li>
                    <li>
                      <button 
                        onClick={() => navigate(`/intent-group/${intentGroup.id}?tab=testsets`)}
                        className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                      >
                        Test Sets
                      </button>
                    </li>
                    <li className="text-muted-foreground/40">
                      <span>/</span>
                    </li>
                  </>
                )}
                <li>
                  <span className="text-maitai-green font-gc-med">New Test Run</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                New Test Run
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Create a new test run to evaluate model performance against your test set
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Progress Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Progress Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-gc-med text-foreground">
                    Step {currentStep + 1} of {steps.length}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {steps[currentStep].title}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-gc-med text-foreground">
                    {completedSteps}/{steps.length} complete
                  </div>
                  <div className="flex items-center gap-1">
                    {steps.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          "w-2 h-2 rounded-full transition-colors",
                          index < currentStep ? "bg-maitai-green" : 
                          index === currentStep ? "bg-maitai-green/60" : "bg-muted"
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Step Navigation */}
              <div className="flex space-x-8">
                {steps.map((step, index) => (
                  <div
                    key={step.key}
                    className={cn(
                      "flex items-center gap-3 text-sm transition-colors",
                      index === currentStep
                        ? "text-maitai-green"
                        : index < currentStep
                        ? "text-foreground"
                        : "text-muted-foreground"
                    )}
                  >
                    <div className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full text-xs font-gc-med transition-colors",
                      index === currentStep
                        ? "bg-maitai-green text-white"
                        : index < currentStep
                        ? "bg-maitai-green text-white"
                        : "bg-muted text-muted-foreground"
                    )}>
                      {index < currentStep ? (
                        <CheckIcon className="w-3 h-3" />
                      ) : (
                        <span>{index + 1}</span>
                      )}
                    </div>
                    <span className="font-gc-reg">{step.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step Content Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {steps[currentStep].component}
          </CardContent>
        </Card>

        {/* Navigation Card */}
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    disabled={loading}
                    className="gap-2"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Previous Step
                  </Button>
                )}
              </div>
              <div>
                {currentStep < steps.length - 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed() || loading}
                    className="gap-2"
                  >
                    Continue
                    <ChevronRightIcon className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    disabled={!canProceed() || loading}
                    onClick={createTestRun}
                    className="gap-2"
                  >
                    {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
                    {loading ? "Creating test run..." : (
                      <>
                        <PlayIcon className="w-4 h-4" />
                        Create Test Run
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TestRunNew;
